import { toast } from "react-toastify";
import { buildCharacterizationCsvSummaryFiles } from "./buildCharacterizationCsvFiles";
import {
  downloadCsvFiles,
  fetchCsvExportDatasets,
  formatCsvReportDate,
  getMaterialCatalogFromStorage,
  getPeriodProducerName,
  isPeriodExportable,
  resolveClientThresholdCm,
} from "./csvExportShared";

/**
 * Summary CSV export for multiple characterization reports (one row per report).
 * rows: [{ period, container, superContainer }]
 *
 * useLatestCoefficients defaults to false: list/bulk export has no per-row coefficient
 * toggle (unlike the detail page). Callers such as CharacterizationList intentionally
 * omit it so every row uses historical coefficients.
 */
export const exportCsvSummaryFromRows = async ({
  rows,
  i18n,
  t,
  onProgress,
  useLatestCoefficients = false,
}) => {
  const reportProgress = (percent, labelKey) => {
    onProgress?.({
      percent,
      label: labelKey ? t(labelKey) : "",
    });
  };

  const exportable = (rows || []).filter(
    (row) => row?.period && row?.container && isPeriodExportable(row.period),
  );

  if (exportable.length === 0) {
    toast.error(
      t("No exportable reports") ||
        t("No data available for export") ||
        "Aucun rapport exportable",
    );
    return;
  }

  try {
    reportProgress(5, "Loading...");
    const materialCatalog = getMaterialCatalogFromStorage();
    const reportRows = [];

    for (let index = 0; index < exportable.length; index += 1) {
      const { period, container, superContainer } = exportable[index];
      const percent = 10 + Math.round((index / exportable.length) * 75);
      reportProgress(percent, "Loading...");

      const { regulationType, clientThresholdData, decreeData } =
        await fetchCsvExportDatasets({
          period,
          container,
          superContainer,
          i18n,
          useLatestCoefficients,
        });

      if (!clientThresholdData && !decreeData) {
        continue;
      }

      reportRows.push({
        dateLabel: formatCsvReportDate(
          period.startDate || period.start || period._time,
        ),
        producerName: getPeriodProducerName(period),
        period,
        regulationType,
        clientThresholdData: clientThresholdData || decreeData,
        decreeData: decreeData || clientThresholdData,
      });
    }

    if (reportRows.length === 0) {
      toast.error(t("No data available for export"));
      return;
    }

    reportProgress(90, "Loading...");
    const clientThresholdCm = await resolveClientThresholdCm();
    const csvFiles = buildCharacterizationCsvSummaryFiles({
      reportRows,
      t,
      locale: i18n.language,
      clientThresholdCm,
      materialCatalog,
    });

    if (csvFiles.length === 0) {
      toast.error(t("No data available for export"));
      return;
    }

    reportProgress(95, "Loading...");
    await downloadCsvFiles(csvFiles);
    reportProgress(100, "Loading...");
    toast.success(
      `${csvFiles.length} ${t("downloaded_files") || "fichiers téléchargés"}`,
    );
  } catch (error) {
    console.error("Error generating summary CSV:", error);
    toast.error(t("Error generating CSV"));
  }
};
